import React, { useState, useEffect } from 'react';
import { MdCheckCircle, MdAttachMoney, MdDescription, MdNotes, MdToday } from 'react-icons/md';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import AccountSelect from './AccountSelect';
import DatePicker from './DatePicker';
import CurrencyInput from './CurrencyInput';
import './EffectuateTransactionModal.css';

function EffectuateTransactionModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  transaction,
  accounts
}) {
  const [formData, setFormData] = useState({
    amount: '',
    date: '',
    account_id: '',
    description: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (transaction && isOpen) {
      // Normalizar a data para formato YYYY-MM-DD (remover timestamp se houver)
      let normalizedDate = transaction.date || '';
      if (normalizedDate && normalizedDate.includes('T')) {
        normalizedDate = normalizedDate.split('T')[0];
      }
      
      setFormData({
        amount: transaction.amount?.toString() || '',
        date: normalizedDate,
        account_id: transaction.account_id || '',
        description: transaction.description || '',
        notes: transaction.notes || ''
      });
      setErrors({});
    }
  }, [transaction, isOpen]);

  if (!isOpen || !transaction) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validação
    const validationErrors = {};
    if (!formData.description || formData.description.trim() === '') {
      validationErrors.description = 'A descrição é obrigatória';
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      validationErrors.amount = 'O valor deve ser maior que zero';
    }
    if (!formData.account_id) {
      validationErrors.account_id = 'A conta é obrigatória';
    }
    if (!formData.date) {
      validationErrors.date = 'A data é obrigatória';
    }
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Chamar callback com os dados
    onConfirm({
      ...formData,
      amount: parseFloat(formData.amount)
    });
    onClose();
  };

  const handleCancel = () => {
    setFormData({
      amount: '',
      date: '',
      account_id: '',
      description: '',
      notes: ''
    });
    setErrors({});
    onClose();
  };

  const setToday = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    setFormData({ ...formData, date: today });
  };

  const formatDateDisplay = (dateString) => {
    try {
      if (!dateString) return '';
      const date = dateString.includes('T') 
        ? new Date(dateString) 
        : new Date(dateString + 'T00:00:00');
      return format(date, "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="effectuate-modal-overlay" onClick={handleCancel}>
      <div className="effectuate-modal" onClick={(e) => e.stopPropagation()}>
        <div className="effectuate-modal-header">
          <div className="modal-header-icon">
            <MdCheckCircle size={32} color="#28a745" />
          </div>
          <div>
            <h2 className="effectuate-modal-title">Liquidar Transação</h2>
            <p className="effectuate-modal-subtitle">
              Marcar como pago e ajustar os dados se necessário
            </p>
          </div>
          <button className="modal-close-btn" onClick={handleCancel}>
            ✕
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="effectuate-modal-form">
          <div className="effectuate-modal-content">
            <div className="form-group with-icon">
              <label>Descrição</label>
              <div className="input-wrapper">
                <MdDescription className="input-icon" size={20} />
                <input
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Ex: Compra no supermercado"
                  className={errors.description ? 'error' : ''}
                />
              </div>
              {errors.description && (
                <span className="error-message">{errors.description}</span>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <CurrencyInput
                  value={formData.amount}
                  onChange={(amount) => setFormData({ ...formData, amount })}
                  label="Valor"
                  required
                  hasError={!!errors.amount}
                />
                {errors.amount && (
                  <span className="error-message">{errors.amount}</span>
                )}
              </div>
              
              <div className="form-group date-group">
                <div className="date-field-header">
                  <label>Data de Pagamento</label>
                  <button
                    type="button"
                    className="btn-use-today"
                    onClick={setToday}
                    title="Usar data de hoje"
                  >
                    <MdToday size={14} />
                    Usar Hoje
                  </button>
                </div>
                <DatePicker
                  value={formData.date}
                  onChange={(date) => setFormData({ ...formData, date })}
                  required
                  hasError={!!errors.date}
                />
                {transaction?.date && formData.date === transaction.date && (
                  <span className="date-hint">
                    💡 Data da transação original
                  </span>
                )}
                {errors.date && (
                  <span className="error-message">{errors.date}</span>
                )}
              </div>
            </div>

            <div className="form-group">
              <AccountSelect
                accounts={accounts}
                selectedId={formData.account_id}
                onSelect={(accountId) => setFormData({ ...formData, account_id: accountId })}
                label="Conta"
                hasError={!!errors.account_id}
              />
              {errors.account_id && (
                <span className="error-message">{errors.account_id}</span>
              )}
            </div>

            <div className="form-group with-icon">
              <label>Observações (opcional)</label>
              <div className="input-wrapper">
                <MdNotes className="input-icon textarea-icon" size={20} />
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="3"
                  placeholder="Adicione observações sobre o pagamento..."
                  className={errors.notes ? 'error' : ''}
                />
              </div>
              {errors.notes && (
                <span className="error-message">{errors.notes}</span>
              )}
            </div>
          </div>

          <div className="effectuate-modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCancel}
            >
              Cancelar
            </button>
            <button type="submit" className="btn-primary success">
              <MdCheckCircle size={20} />
              Confirmar Pagamento
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EffectuateTransactionModal;
